import { useRouter } from 'next/router'
import { useEffect, useMemo, useRef, useState } from 'react'

import { api, toggle, getCollapsed, clearCollapsed, checkCollapsed, manageCollapsed } from 'utils'
import { LISTINGALGOLIA, UTENTE } from 'utils/endpoint'

import Hit from 'components/algolia/Hit'
import Filterbar from 'components/atomic/Filterbar'
import Switch from 'components/atomic/Switch'
import { BaseRefinementList } from 'components/algolia/RefinementList'
import Chip from 'components/atomic/Chip'
import SlickSlider from 'react-slick'
import Pagination from 'components/atomic/Pagination'
import GenericCardPlaceholder from 'components/loading/GenericCardPlaceholder'
import AlgoliaFilterPlaceholder from 'components/loading/AlgoliaFilterPlaceholder'
import SwitchPlaceholder from 'components/loading/SwitchPlaceholder'
import DropdownAlgolia from 'components/atomic/DropdownAlgolia'
import { sendProductImpressionGTM } from '../utils/gtm'

const settings = {
  infinite: false,
  slidesToShow: 1,
  slidesToScroll: 1,
  variableWidth: true,
  autoplay: false,
  arrows: false,
  centerPadding: '15px',
}

const tipologie = ['articolo', 'lezione', 'strumento', 'raccolta']
const defaultOrder = [
  'visibilita', // custom
  'Ordine di scuola',
  'Classe',
  'Eta',
  'Disciplina',
  'models', // custom
  'Tipo Strumento',
]

function ListingAlgolia(props) {
  const {
    filtriInitial = [],
    numeroRivista = null,
    riviste = null,
    models = tipologie,
    onlyOneType = false,
    filtriDisponibili = defaultOrder,
    className = '',
  } = props

  const router = useRouter()

  const [items, setItems] = useState([])
  const [attributi, setAttributi] = useState([])
  const [pagination, setPagination] = useState({ page: 1, num_pages: 1, page_range: [] })
  const [ordinamenti, setOrdinamenti] = useState([])
  const [tipi, setTipi] = useState([])
  const [visibilita, setVisibilita] = useState([])

  const [params, setParams] = useState({
    tipi: router.query.models?.split('/') || models,
    visibilita: router.query.membership?.split('/') || ['member', 'tutti'],
    ordine: '-created',
    page: 1,
    filtriId: router.query.filtri_id?.split('/').filter(Number).map(Number) || [],
  })

  const [loading, setLoading] = useState(true)
  const [grid, setGrid] = useState(false)

  const mounted = useRef(false)

  function handleChangeOrder(value) {
    const ordine = typeof value === 'string' ? value : value.value // se valore diretto o oggetto

    setParams({ ...params, ordine })
  }

  function handleChangeTipo(value) {
    let tmp = router.query.models?.split('/') || params.tipi

    toggle(tmp, value)

    setParams({ ...params, tipi: tmp })

    manageCollapsed('models')
  }

  function handleChangeVisibilita(value) {
    let tmp = router.query.membership?.split('/') || params.visibilita

    toggle(tmp, value)

    setParams({ ...params, visibilita: tmp })
    manageCollapsed('membership')
  }

  function handleChangePage(value) {
    setParams({ ...params, page: value })
  }

  function handleChangeFilter(value) {
    let tmp = router.query.filtri_id?.split('/').filter(Number).map(Number) || []

    toggle(tmp, value)

    setParams({ ...params, filtriId: tmp })

    manageCollapsed(value)
  }

  function handleChangeFilters() {
    const baseUrl = router.asPath.split('?')[0]

    const filtriId = `filtri_id=${params.filtriId.join('/')}`
    const models = `models=${params.tipi.join('/')}`
    const membership = `membership=${params.visibilita.join('/')}`
    const p = `p=${params.page}`
    const o = `o=${params.ordine}`

    const url = `${baseUrl}?${filtriId}&${models}&${membership}&${p}&${o}`

    router.push(url, undefined, { shallow: true, scroll: false }) // client side transition
  }

  function handleSearch() {
    setLoading(true)

    let queryParams = {
      p: router.query.p || 1,
      o: router.query.o || '-created',
      filtri_id: router.query.filtri_id,
      filtri_initial: filtriInitial?.join('/'), // non deve mai cambiare (es. ordine di scuola, disciplina, tema)
      models: router.query.models || params.tipi.join('/'),
      membership: router.query.membership || params.visibilita.join('/'),
    }

    if (numeroRivista) queryParams['numero_rivista'] = numeroRivista

    api
      .get(LISTINGALGOLIA, { params: queryParams })
      .then((response) => {
        sendProductImpressionGTM(
          response.data.listing.elementi,
          queryParams.filtri_id ? 'ordinamento_filtri' : 'search_result'
        )
        setItems(response.data.listing.elementi)
        setAttributi(response.data.listing.attributi)
        setPagination({
          page: response.data.listing.page,
          num_pages: response.data.listing.num_pages,
          page_range: response.data.listing.page_range,
        })
        setOrdinamenti(response.data.listing.ordinamenti)
        setTipi(response.data.listing.tipi)
        setVisibilita(response.data.listing.visibilita)
        setLoading(false)
      })
      .catch((error) => console.log(error))
  }

  useEffect(() => {
    handleSearch()
  }, [router.query])

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true
      return
    }
    handleChangeFilters()
  }, [params])

  useEffect(() => {
    if (items.length) checkTools(items)
  }, [attributi])

  useEffect(() => {
    window.addEventListener('beforeunload', clearCollapsed)
    return () => window.removeEventListener('beforeunload', clearCollapsed)
  }, [])

  function checkTools(items) {
    const params = items.map(({ object_id, model }) => ({
      content_type_id: model,
      object_id: object_id,
    }))

    api.get(UTENTE + 'tools/', { params }).then((response) => {
      const enhancedItems = items.map((item) => {
        const current = response.data.find(
          (e) => e.content_type_id === item.model && e.object_id === item.object_id
        )

        const { saved, following, file_url, pdf_file } = current

        return { extra: { ...item.extra, file_url, pdf_file }, saved, following, ...item }
      })

      setItems(enhancedItems)
    })
  }

  const customFilters = useMemo(
    () => ({
      visibilita: (
        <div className="ricerca__listing__filters__filter">
          <BaseRefinementList
            items={visibilita.map((t) => ({ ...t, value: t.valore, label: t.valore }))}
            placeholder={'Free/io+'}
            refine={handleChangeVisibilita}
            noAlgolia
            collapsed={checkCollapsed('membership')}
          />
        </div>
      ),
      models: (
        <div className="ricerca__listing__filters__filter">
          <BaseRefinementList
            items={tipi.map((t) => ({ ...t, value: t.valore, label: t.valore }))}
            placeholder={'Tipo di contenuto'}
            refine={handleChangeTipo}
            noAlgolia
            disabled={onlyOneType}
            index={999} // accrocchio per mostrarlo collapsed
            collapsed={checkCollapsed('models')}
          />
        </div>
      ),
    }),
    [tipi, visibilita]
  )

  const orderedFilters = useMemo(
    () =>
      filtriDisponibili.map((filtro, index) => {
        const current = attributi.find((a) => a.nome === filtro)
        if (current) {
          return (
            <div className="ricerca__listing__filters__filter">
              <BaseRefinementList
                items={current.valori_prefetch.map((v) => ({ label: v.nome, ...v }))}
                placeholder={current.nome}
                refine={handleChangeFilter}
                index={index}
                collapsed={
                  !current.valori_prefetch.map((v) => String(v.id)).includes(getCollapsed())
                }
                checkCount
              />
            </div>
          )
        } else {
          return customFilters[filtro]
        }
      }),
    [attributi, tipi, visibilita]
  )

  const ordini = useMemo(
    () => (
      <div className="ricerca__listing__filters__filter">
        <BaseRefinementList
          items={ordinamenti}
          placeholder={'Ordina per'}
          refine={handleChangeOrder}
          isOrdine
        />
      </div>
    ),
    [ordinamenti]
  )

  const chips = useMemo(
    () =>
      attributi
        .map((a) => a.valori_prefetch.filter((v) => v.selezionato))
        .flat()
        .filter((v) => !filtriInitial.includes(v.id)) // non rimuovibile se in filtriInitial
        .map((v) => <Chip label={v.nome} onClick={() => handleChangeFilter(v.id)} key={v.id} />),
    [attributi]
  )

  return (
    <>
      <div className={`${className ? className : 'ricerca__content'}`}>
        <div className="w-container">
          {riviste ? <div className="listing_riviste_element">{riviste}</div> : null}
          <div className="ricerca__listing">
            <div className="ricerca__listing__filters">
              <p className="ricerca__listing__filters__title ricerca__listing__filters__title--listing">
                Filtra per
              </p>
              <div className="ricerca__listing__filters__chips">{chips}</div>
              {loading
                ? [...Array(6).keys()].map((index) => <AlgoliaFilterPlaceholder key={index} />)
                : orderedFilters}
            </div>
            <div className={`ricerca__listing__list ${grid ? 'horizontal' : ''}`}>
              {loading ? (
                <>
                  <div className="ricerca__listing__list__top ricerca__listing__list__top--loading">
                    <SwitchPlaceholder />
                  </div>
                  <div className="ricerca__listing__list__loading">
                    {[...Array(4).keys()].map((index) => (
                      <article key={index}>
                        <GenericCardPlaceholder />
                      </article>
                    ))}
                  </div>
                </>
              ) : items.length ? (
                <>
                  <div className="ricerca__listing__list__top">
                    <label
                      htmlFor="ricerca_ordine"
                      id="ordine_label"
                      className="ricerca__listing__list__top__title"
                    >
                      Ordina per
                    </label>
                    <DropdownAlgolia
                      inputId="ricerca_ordine"
                      instanceId={'ordini'}
                      placeholder={ordinamenti.find((o) => o.selected).label}
                      options={ordinamenti}
                      onChange={handleChangeOrder}
                    />
                    <Switch isChecked={!grid} handleToggle={() => setGrid(!grid)} />
                  </div>
                  <div className="ais-Hits-list">
                    {items.map((hit, index) => (
                      <Hit
                        hit={{
                          ...hit,
                          extra: {
                            ...hit.extra,
                            membership: hit.membership_index,
                            utenti: hit.autori,
                          },
                        }}
                        horizontal={grid}
                        key={hit.objectID}
                        position={index + 1}
                      />
                    ))}
                  </div>
                  {pagination.page_range.length ? (
                    <Pagination
                      pagination={pagination}
                      currentPage={pagination.page}
                      handleChangePage={handleChangePage}
                    />
                  ) : null}
                </>
              ) : (
                <p className="ricerca__empty">Nessun risultato trovato</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <Filterbar filtri={orderedFilters} ordini={ordini}>
        {chips.length ? (
          <div className="ricerca-related">
            <div className="ricerca-related__tags">
              <SlickSlider {...settings}>{chips}</SlickSlider>
            </div>
          </div>
        ) : null}
      </Filterbar>
    </>
  )
}

export default ListingAlgolia
